/**
 * Audit Log – system admin only. Lists changes to venues, matches, tournaments, pricing, etc.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useRole } from '../contexts/RoleContext';
import { useNavigate } from 'react-router-dom';
import { getAuditLogs } from '../api/auditApi';
import { AUDIT_ACTIONS, AUDIT_ENTITIES } from '../services/auditLogService';
import './AuditLog.css';

const ENTITY_LABELS = {
  [AUDIT_ENTITIES.VENUE]: 'Venue',
  [AUDIT_ENTITIES.COURT]: 'Court',
  [AUDIT_ENTITIES.VENUE_BOOKING]: 'Venue booking',
  [AUDIT_ENTITIES.VENUE_BLOCK]: 'Venue block',
  [AUDIT_ENTITIES.VENUE_ADMIN]: 'Venue admin',
  [AUDIT_ENTITIES.TOURNAMENT]: 'Tournament',
  [AUDIT_ENTITIES.MATCH]: 'Match',
  [AUDIT_ENTITIES.REGISTRATION]: 'Registration',
  [AUDIT_ENTITIES.USER]: 'User',
  [AUDIT_ENTITIES.APARTMENT]: 'Apartment',
  [AUDIT_ENTITIES.PRICING]: 'Pricing',
  [AUDIT_ENTITIES.USER_ACTIVITY]: 'User activity',
};

const ACTION_LABELS = {
  [AUDIT_ACTIONS.CREATE]: 'Created',
  [AUDIT_ACTIONS.UPDATE]: 'Updated',
  [AUDIT_ACTIONS.DELETE]: 'Deleted',
  sign_in: 'Sign in',
  app_access: 'App access',
  page_view: 'Page view',
  button_click: 'Button click',
  nav_click: 'Nav click',
  match_start: 'Match start',
  match_complete: 'Match complete',
  reschedule: 'Reschedule',
  profile_edit: 'Profile edit',
  tournament_register: 'Tournament register',
  login_as_start: 'Login as (start)',
  login_as_stop: 'Login as (stop)',
  share_stats: 'Share stats',
  leaderboard_suppress: 'Leaderboard suppress',
};

const LOG_LIMIT = 100;

function getUserLabel(log) {
  const name = log.displayName || log.userEmail || log.userId || 'Unknown';
  return String(name).trim() || 'Unknown';
}

function toDatetimeLocal(d) {
  const date = d instanceof Date ? d : new Date(d);
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const h = String(date.getHours()).padStart(2, '0');
  const min = String(date.getMinutes()).padStart(2, '0');
  return `${y}-${m}-${day}T${h}:${min}`;
}

function getDefaultDateRange() {
  const now = new Date();
  const from = new Date(now);
  from.setDate(from.getDate() - 7);
  from.setSeconds(0, 0);
  return { from: toDatetimeLocal(from), to: toDatetimeLocal(now) };
}

export default function AuditLog() {
  const { isSuperAdmin } = useRole();
  const navigate = useNavigate();
  const defaultRange = React.useMemo(() => getDefaultDateRange(), []);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterEntity, setFilterEntity] = useState('');
  const [filterAction, setFilterAction] = useState('');
  const [filterUserId, setFilterUserId] = useState('');
  const [filterExcludeUserId, setFilterExcludeUserId] = useState('');
  const [userSearch, setUserSearch] = useState('');
  const [dateFrom, setDateFrom] = useState(defaultRange.from);
  const [dateTo, setDateTo] = useState(defaultRange.to);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [hasPrevPage, setHasPrevPage] = useState(false);
  const pageStartCursorRef = useRef(null);
  const pageEndCursorRef = useRef(null);

  const fetchLogs = useCallback(async (cursor = null, direction = 'initial') => {
    if (!isSuperAdmin) return;
    setLoading(true);
    try {
      const fromDate = new Date(dateFrom);
      const toDate = new Date(dateTo);
      if (toDate < fromDate) {
        setLogs([]);
        setHasNextPage(false);
        setHasPrevPage(false);
        pageStartCursorRef.current = null;
        pageEndCursorRef.current = null;
        return;
      }
      const result = await getAuditLogs({
        dateFrom,
        dateTo,
        filterExcludeUserId: filterExcludeUserId || undefined,
        cursor: cursor || undefined,
        direction,
      });
      setLogs(result.logs || []);
      setHasNextPage(result.hasNextPage ?? false);
      setHasPrevPage(result.hasPrevPage ?? false);
      pageStartCursorRef.current = result.pageStartCursor ?? null;
      pageEndCursorRef.current = result.pageEndCursor ?? null;
    } catch (err) {
      console.error('Error fetching audit log:', err);
      setLogs([]);
      setHasNextPage(false);
      setHasPrevPage(false);
      pageStartCursorRef.current = null;
      pageEndCursorRef.current = null;
    } finally {
      setLoading(false);
    }
  }, [isSuperAdmin, dateFrom, dateTo, filterExcludeUserId]);

  const fetchNextPage = useCallback(() => {
    const lastCursor = pageEndCursorRef.current;
    if (lastCursor && hasNextPage) fetchLogs(lastCursor, 'next');
  }, [fetchLogs, hasNextPage]);

  const fetchPrevPage = useCallback(() => {
    const firstCursor = pageStartCursorRef.current;
    if (firstCursor && hasPrevPage) fetchLogs(firstCursor, 'prev');
  }, [fetchLogs, hasPrevPage]);

  const handleLoad = useCallback(() => {
    pageStartCursorRef.current = null;
    pageEndCursorRef.current = null;
    fetchLogs(null, 'initial');
  }, [fetchLogs]);

  useEffect(() => {
    if (!isSuperAdmin) {
      navigate('/dashboard');
      return;
    }
    handleLoad();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- initial load only; refetch via Load button
  }, [isSuperAdmin, navigate]);

  const uniqueUsers = React.useMemo(() => {
    const seen = new Set();
    const users = [];
    for (const log of logs) {
      const uid = log.userId || null;
      if (uid && !seen.has(uid)) {
        seen.add(uid);
        const label = getUserLabel(log);
        users.push({
          userId: uid,
          label: label && label !== 'Unknown' ? label : `User ${uid.slice(0, 12)}${uid.length > 12 ? '…' : ''}`,
        });
      }
    }
    users.sort((a, b) => (a.label || '').localeCompare(b.label || '', undefined, { sensitivity: 'base' }));
    if (filterExcludeUserId && !users.some((u) => u.userId === filterExcludeUserId)) {
      users.push({
        userId: filterExcludeUserId,
        label: `Excluded: ${filterExcludeUserId.slice(0, 12)}${filterExcludeUserId.length > 12 ? '…' : ''}`,
      });
    }
    return users;
  }, [logs, filterExcludeUserId]);

  const filtered = logs.filter((log) => {
    if (filterEntity && log.entityType !== filterEntity) return false;
    if (filterAction && log.action !== filterAction) return false;
    if (filterUserId && log.userId !== filterUserId) return false;
    if (filterExcludeUserId && log.userId === filterExcludeUserId) return false;
    if (userSearch.trim()) {
      const q = userSearch.trim().toLowerCase();
      const email = (log.userEmail || '').toLowerCase();
      const name = (log.displayName || '').toLowerCase();
      const uid = (log.userId || '').toLowerCase();
      const matches = email.includes(q) || name.includes(q) || uid.includes(q);
      if (!matches) return false;
    }
    return true;
  });

  if (!isSuperAdmin) {
    return null;
  }

  if (loading) {
    return <div className="loading">Loading audit log...</div>;
  }

  return (
    <div className="audit-log-container">
      <div className="audit-log-header">
        <h1 className="page-title">Audit Log</h1>
        <p className="page-subtitle">
          All actions by all users: app access (every 30 min window), sign-in, page views, and changes to venues, matches, tournaments, pricing, and other system data. Super admin only.
        </p>
      </div>

      <div className="audit-log-date-range">
        <label>
          From
          <input
            type="datetime-local"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="audit-log-datetime-input"
          />
        </label>
        <label>
          To
          <input
            type="datetime-local"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="audit-log-datetime-input"
          />
        </label>
        <button type="button" onClick={handleLoad} disabled={loading} className="audit-log-load-btn">
          {loading ? 'Loading…' : 'Load'}
        </button>
      </div>
      <div className="audit-log-filters">
        <label>
          Entity
          <select value={filterEntity} onChange={(e) => setFilterEntity(e.target.value)}>
            <option value="">All</option>
            {Object.entries(ENTITY_LABELS).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
        </label>
        <label>
          Action
          <select value={filterAction} onChange={(e) => setFilterAction(e.target.value)}>
            <option value="">All</option>
            {Object.entries(ACTION_LABELS).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
        </label>
        <label className="audit-log-filter-user">
          User
          <select value={filterUserId} onChange={(e) => setFilterUserId(e.target.value)}>
            <option value="">All users</option>
            {uniqueUsers.map((u) => (
              <option key={u.userId} value={u.userId}>{u.label}</option>
            ))}
          </select>
        </label>
        <label className="audit-log-filter-user">
          Exclude user
          <select value={filterExcludeUserId} onChange={(e) => setFilterExcludeUserId(e.target.value)}>
            <option value="">None</option>
            {uniqueUsers.map((u) => (
              <option key={u.userId} value={u.userId}>Exclude: {u.label}</option>
            ))}
          </select>
        </label>
        <label className="audit-log-filter-search">
          Search user
          <input
            type="search"
            value={userSearch}
            onChange={(e) => setUserSearch(e.target.value)}
            placeholder="Name, email, or user ID…"
            className="audit-log-user-search-input"
          />
        </label>
      </div>
      <p className="audit-log-range-hint">
        Showing up to {LOG_LIMIT} logs per page in selected date range. Use Load to apply. Exclude user excludes that user from the fetch.
      </p>

      <div className="audit-log-pagination">
        <button
          type="button"
          onClick={fetchPrevPage}
          disabled={loading || !hasPrevPage}
          className="audit-log-page-btn"
        >
          ← Previous
        </button>
        <button
          type="button"
          onClick={fetchNextPage}
          disabled={loading || !hasNextPage}
          className="audit-log-page-btn"
        >
          Next →
        </button>
      </div>

      <div className="audit-log-table-wrap">
        <table className="audit-log-table">
          <thead>
            <tr>
              <th>Time</th>
              <th>Action</th>
              <th>Entity</th>
              <th>ID</th>
              <th>User</th>
              <th>Details</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={6} className="audit-log-empty">No audit entries yet.</td>
              </tr>
            ) : (
              filtered.map((log) => (
                <tr key={log.id}>
                  <td className="audit-log-time">
                    {log.timestamp && (log.timestamp.toDate ? log.timestamp.toDate().toLocaleString() : new Date(log.timestamp).toLocaleString())}
                  </td>
                  <td>{ACTION_LABELS[log.action] || log.action}</td>
                  <td>{ENTITY_LABELS[log.entityType] || log.entityType}</td>
                  <td className="audit-log-id">{log.entityId || '—'}</td>
                  <td>{getUserLabel(log) || '—'}</td>
                  <td className="audit-log-details">
                    {log.entityType === AUDIT_ENTITIES.USER_ACTIVITY && (
                      <span title={log.metadata ? JSON.stringify(log.metadata) : ''}>
                        {log.action || '—'}
                        {log.metadata?.page && ` · ${log.metadata.page}`}
                        {log.metadata?.label && ` · ${log.metadata.label}`}
                      </span>
                    )}
                    {log.entityType !== AUDIT_ENTITIES.USER_ACTIVITY && log.metadata && Object.keys(log.metadata).length > 0 && (
                      <span title={JSON.stringify(log.metadata)}>
                        {log.metadata.name || log.metadata.venueName || (log.changes && JSON.stringify(log.changes)) || '—'}
                      </span>
                    )}
                    {log.entityType !== AUDIT_ENTITIES.USER_ACTIVITY && log.changes && !log.metadata?.name && !log.metadata?.venueName && (
                      <span title={JSON.stringify(log.changes)}>{JSON.stringify(log.changes)}</span>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
